// Where the agent's face is against the nose target, from face-mesh landmarks. Pure geometry:
// the recorder's hook feeds it samples and the overlay shows the result.

export interface Point {
    x: number;
    y: number;
}

// Landmarks in the camera frame's own 0–1 coordinates.
export interface FaceSample {
    nose: Point;
    chin: Point;
}

export interface FaceDetector {
    detect(video: HTMLVideoElement, timestampMs: number): FaceSample | null;
    close(): void;
}

export interface FrameSize {
    width: number;
    height: number;
}

// The drawn target: the ring's centre and the bottom bracket, % of the visible box height.
export interface AlignmentTarget {
    nose: number;
    zoneBottom: number;
}

export type Hint = "Move closer" | "Move back" | "Move left" | "Move right" | "Move up" | "Move down";

export type Alignment = { status: "searching" } | { status: "hint"; hint: Hint } | { status: "good" };

// The captioned recorder shows the camera frame object-cover in a 9:16 box.
export const BOX_ASPECT = 9 / 16;

// Face-mesh indices: nose tip and chin.
const NOSE_TIP = 1;
const CHIN = 152;

interface Tolerance {
    side: number; // % of the box width either side of centre
    vertical: number; // % of the box height either side of the ring
    sizeMin: number; // nose-to-chin as a fraction of the bracket height
    sizeMax: number;
}

// Generous: the ring is a target, not a lock.
const ARRIVE: Tolerance = { side: 10, vertical: 8, sizeMin: 0.6, sizeMax: 1.5 };
// Once lined up, the face may drift this far before the ring goes amber again, so holding still on the
// line does not flicker.
const HOLD: Tolerance = { side: 13, vertical: 11, sizeMin: 0.55, sizeMax: 1.6 };

// A frame point (0–1) to % of the visible box: the crop drops the frame's sides when it is wider
// than the box and its top and bottom when it is taller.
export function coverMap(p: Point, frameWidth: number, frameHeight: number, boxAspect = BOX_ASPECT): { x: number; y: number } {
    const frameAspect = frameWidth / frameHeight;
    if (frameAspect > boxAspect) {
        const visibleWidth = frameHeight * boxAspect;
        const offset = (frameWidth - visibleWidth) / 2;
        return { x: ((p.x * frameWidth - offset) / visibleWidth) * 100, y: p.y * 100 };
    }
    const visibleHeight = frameWidth / boxAspect;
    const offset = (frameHeight - visibleHeight) / 2;
    return { x: p.x * 100, y: ((p.y * frameHeight - offset) / visibleHeight) * 100 };
}

export function toSample(landmarks: ReadonlyArray<Point> | undefined): FaceSample | null {
    if (!landmarks || landmarks.length <= CHIN) return null;
    const nose = landmarks[NOSE_TIP];
    const chin = landmarks[CHIN];
    return { nose: { x: nose.x, y: nose.y }, chin: { x: chin.x, y: chin.y } };
}

// `held`: the ring is already green, so judge against the wider hold margin.
export function assessAlignment(sample: FaceSample | null, frame: FrameSize, target: AlignmentTarget, mirrored: boolean, held = false): Alignment {
    if (!sample) return { status: "searching" };
    const t = held ? HOLD : ARRIVE;
    const nose = coverMap(sample.nose, frame.width, frame.height);
    const chin = coverMap(sample.chin, frame.width, frame.height);
    // Size first: far away, the position hints are noise.
    const size = (chin.y - nose.y) / (target.zoneBottom - target.nose);
    if (size < t.sizeMin) return { status: "hint", hint: "Move closer" };
    if (size > t.sizeMax) return { status: "hint", hint: "Move back" };
    // Sideways in on-screen terms: the front camera preview is mirrored.
    const screenX = mirrored ? 100 - nose.x : nose.x;
    const dx = screenX - 50;
    if (dx > t.side) return { status: "hint", hint: "Move left" };
    if (dx < -t.side) return { status: "hint", hint: "Move right" };
    const dy = nose.y - target.nose;
    if (dy > t.vertical) return { status: "hint", hint: "Move up" };
    if (dy < -t.vertical) return { status: "hint", hint: "Move down" };
    return { status: "good" };
}
